import React from 'react';
import { useLocation } from 'react-router-dom';
import HeroSection from '@/components/sections/HeroSection';
import TrustSection from '@/components/sections/TrustSection';
import FeaturesSection from '@/components/sections/FeaturesSection';
import HowItWorksSection from '@/components/sections/HowItWorksSection';
import UseCasesSection from '@/components/sections/UseCasesSection';
import PricingSection from '@/components/sections/PricingSection';
import FaqSection from '@/components/sections/FaqSection';
import CtaSection from '@/components/sections/CtaSection';
import ApiDocsSection from '@/components/sections/ApiDocsSection';
import InfrastructureSection from '@/components/sections/InfrastructureSection';
import FeatureMatrixSection from '@/components/sections/FeatureMatrixSection';
import { SchemaOrg } from '@/components/utils/SchemaOrg';

const HomePage = ({ onActionClick }) => {
  const location = useLocation();
  const pageTitle = "WebFiny: Smart PDF APIs & Document Automation Platform";
  const pageDescription = "Empower your applications with WebFiny's fast, secure, and scalable PDF processing APIs. Merge, extract, compress, and automate documents effortlessly.";
  const canonicalUrl = `${window.location.origin}${location.pathname}`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is WebFiny?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "WebFiny is a comprehensive suite of PDF APIs designed for developers and businesses to integrate powerful document processing capabilities into their applications. We offer services like PDF merging, splitting, compression, conversion, and data extraction."
        }
      },
      {
        "@type": "Question",
        "name": "Who can use WebFiny APIs?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our APIs are built for developers, startups, and enterprises across various industries including legal, healthcare, real estate, publishing, and e-commerce. Anyone needing to automate PDF workflows can benefit."
        }
      },
      {
        "@type": "Question",
        "name": "Is there a free trial or a free tier?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, WebFiny offers a free tier that allows you to test our APIs and process a limited number of documents per month. For higher volumes and advanced features, you can choose from our flexible paid plans."
        }
      },
      {
        "@type": "Question",
        "name": "How secure is WebFiny?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Security is our top priority. We use industry-standard encryption for data in transit and at rest. Our infrastructure is designed for high availability and data protection. We are compliant with major data privacy regulations."
        }
      },
      {
        "@type": "Question",
        "name": "What kind of support do you offer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We offer comprehensive documentation, API references, and email support for all users. Enterprise clients receive dedicated support channels and SLAs."
        }
      }
    ]
  };
  
  const productSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Starter Plan",
      "description": "Ideal for individuals and small projects getting started with PDF automation.",
      "brand": {
        "@type": "Brand",
        "name": "WebFiny"
      },
      "offers": {
        "@type": "Offer",
        "price": "9.99",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": "https://webfiny.com/#pricing"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Developer Plan",
      "description": "Perfect for developers and growing businesses needing more capacity and features.",
      "brand": {
        "@type": "Brand",
        "name": "WebFiny"
      },
      "offers": {
        "@type": "Offer",
        "price": "29.99",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": "https://webfiny.com/#pricing"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Business Plan",
      "description": "Comprehensive solution for established businesses with high volume and support needs.",
      "brand": {
        "@type": "Brand",
        "name": "WebFiny"
      },
      "offers": {
        "@type": "Offer",
        "price": "79.99",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": "https://webfiny.com/#pricing"
      }
    }
  ];


  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": pageTitle,
            "description": pageDescription,
            "url": canonicalUrl,
            "isPartOf": {
              "@type": "WebSite",
              "url": window.location.origin
            }
          },
          faqSchema,
          ...productSchemas
        ]}
      />
      <HeroSection onPrimaryActionClick={() => onActionClick("Get Started from Hero")} onSecondaryActionClick={() => onActionClick("View Docs from Hero")} />
      <TrustSection />
      <FeaturesSection onActionClick={onActionClick} />
      <HowItWorksSection />
      <UseCasesSection onActionClick={onActionClick} />
      <InfrastructureSection />
      <PricingSection onActionClick={onActionClick} />
      <FeatureMatrixSection />
      <ApiDocsSection onActionClick={onActionClick} />
      <FaqSection />
      <CtaSection onActionClick={onActionClick} />
    </>
  );
};

export default HomePage;